import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, FileText, Layers, Send, Handshake, FileCheck,
  Briefcase, ShieldCheck, Activity, Lock, Eye,
  ClipboardCheck, Users2, Quote,
} from 'lucide-react';
import { StickyMobileCTA } from './StickyMobileCTA';
import { useReveal } from '@/lib/useReveal';

/**
 * /for-borrowers — conforms to GP CAP Public Site Design System v1.
 * Navy surfaces, single .gpc-card system, Fraunces section headings,
 * one primary CTA per section, hairline section transitions.
 *
 * Step labels MUST match the homepage Pathways component exactly:
 *   01 Submit opportunity · 02 Package documents · 03 Controlled distribution
 *   04 Negotiate terms · 05 Execute
 *
 * Phase 105B — Bren Watts broker feedback:
 *   • borrower-facing language simplified;
 *   • "What to include in your initial enquiry" checklist added;
 *   • "For brokers and referral partners" clarity block added;
 *   • Testimonial section is staged and hidden by default until Bren
 *     provides written approval — set TESTIMONIAL_APPROVED = true to
 *     render it. It uses the softened, outcome-neutral version only.
 */

// Testimonial gate — flip to `true` ONLY after Bren Watts confirms in
// writing that the softened version below may be published to the public
// site. Until then this section renders nothing.
const TESTIMONIAL_APPROVED = true;

const ENQUIRY_CHECKLIST = [
  'Your name and best contact number',
  'Email address',
  'Borrower / company name, if applicable',
  'Funding amount or indicative range',
  'Use of funds',
  'Asset or security details, if relevant',
  'Location of the asset, if relevant',
  'Current debt or finance position',
  'Required timeframe',
  'Current status of the transaction',
  'Any key documents already available',
  'Whether a broker or adviser is involved',
];

const PATHWAY_STEPS = [
  {
    icon: FileText,
    title: 'Submit opportunity',
    body: 'Send the opportunity through a confidential intake — capital requirement, structure, sponsor and asset detail. We acknowledge within one business day and confirm whether the mandate fits the platform.',
  },
  {
    icon: Layers,
    title: 'Package documents',
    body: 'IM, deal points and supporting documents are structured into a clear capital-partner-ready pack. GP Capital reviews underwriting, structure and presentation before any external circulation.',
  },
  {
    icon: Send,
    title: 'Controlled distribution',
    body: 'Selective circulation to a matched, pre-qualified counterparty group under NDA — no broadcast email, no open market. Each counterparty\'s mandate, geography and ticket envelope is matched against the opportunity.',
  },
  {
    icon: Handshake,
    title: 'Negotiate terms',
    body: 'Indicative term sheets, Q&A and revisions are managed through a single workflow with clear audit history. Best-and-final rounds run inside the platform when warranted.',
  },
  {
    icon: FileCheck,
    title: 'Execute',
    body: 'The selected counterparty progresses to documentation, settlement and close. GP Capital coordinates the closing workstream and remains the single point of contact through to drawdown.',
  },
];

const CAPITAL_SOLUTIONS = [
  'Senior debt',
  'Bridge finance',
  'Construction finance',
  'Mezzanine capital',
  'Preferred equity',
  'Structured finance',
  'Special situations',
];

const TRUST_CUES = [
  { icon: Lock, label: 'NDA-gated access' },
  { icon: Eye, label: 'Controlled document preview' },
  { icon: ShieldCheck, label: 'Role-based permissions' },
  { icon: Activity, label: 'Audit-aware workflow' },
];

// ── Small composable building blocks ──────────────────────────────────────

const Section = ({ children, alt, testId, className = '' }) => {
  const [ref, visible] = useReveal({ threshold: 0.1 });
  return (
    <section
      ref={ref}
      className={`gpc-section ${alt ? 'gpc-section--alt' : ''} ${className}`}
      data-testid={testId}
    >
      <div className={`gpc-container gpc-reveal ${visible ? 'is-visible' : ''}`}>{children}</div>
    </section>
  );
};

const StepCard = ({ step, index }) => (
  <div className="gpc-card flex items-start gap-4" data-testid={`borrower-step-${index + 1}`}>
    <div
      className="w-9 h-9 rounded-md flex items-center justify-center shrink-0 mt-0.5"
      style={{ background: 'rgba(201,168,106,0.10)' }}
    >
      <step.icon className="w-4 h-4" style={{ color: 'var(--gpc-gold)' }} />
    </div>
    <div className="min-w-0">
      <div className="flex items-baseline gap-2 mb-1.5">
        <span
          className="gpc-num text-[10px]"
          style={{ color: 'var(--gpc-muted)', letterSpacing: '0.2em' }}
        >
          {String(index + 1).padStart(2, '0')}
        </span>
        <h3 className="gpc-h3" style={{ fontSize: '16px' }}>{step.title}</h3>
      </div>
      <p className="gpc-body" style={{ fontSize: '14px', lineHeight: 1.6 }}>{step.body}</p>
    </div>
  </div>
);

// ── Page ──────────────────────────────────────────────────────────────────

export const ForBorrowersPage = () => (
  <div data-testid="for-borrowers-page-content">
    {/* Hero */}
    <Section testId="borrowers-hero" className="relative overflow-hidden">
      {/* Subtle masked grid mesh — feature sections only per DS §4 */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.55] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(19,35,63,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(19,35,63,0.06) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(60% 70% at 75% 35%, #000 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(60% 70% at 75% 35%, #000 30%, transparent 80%)',
        }}
      />
      <div className="relative max-w-3xl">
        <p className="gpc-eyebrow mb-5">Private Capital · For Borrowers</p>
        <h1
          className="mb-6"
          style={{
            fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
            fontWeight: 400,
            fontSize: 'clamp(2.2rem, 4.6vw, 3.8rem)',
            lineHeight: 1.05,
            letterSpacing: '-0.01em',
            color: 'var(--gpc-ink)',
          }}
        >
          Structured access to <span style={{ color: 'var(--gpc-gold)', fontStyle: 'italic' }}>private capital.</span>
        </h1>
        <p className="gpc-body mb-9 max-w-2xl">
          GP Capital helps businesses and property developers connect with the right private lenders and investors, through a controlled and confidential process.
        </p>
        <p className="gpc-body mb-5">Looking for a residential home loan? <Link to="/home-loans" className="underline">Visit Home Loans</Link>.</p>
        <div className="flex flex-wrap gap-3" data-testid="borrowers-hero-ctas">
          <Link to="/contact" className="gpc-cta-primary" data-testid="borrowers-cta-submit" id="submit-deal-cta-anchor">
            Submit an enquiry
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/portal/login" className="gpc-cta-secondary" data-testid="borrowers-cta-platform">
            Enter platform
          </Link>
        </div>
      </div>
    </Section>

    {/* Capital solutions + positioning */}
    <Section alt testId="borrowers-solutions">
      <div className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <p className="gpc-eyebrow mb-4">Capital solutions</p>
          <h2 className="gpc-h2 mb-5">
            Disciplined distribution into matched <span style={{ color: 'var(--gpc-gold)', fontStyle: 'italic' }}>institutional capital.</span>
          </h2>
          <div className="gpc-body space-y-4">
            <p>
              GP Capital helps businesses and property developers connect with the right private lenders and investors.
            </p>
            <p>
              Rather than acting like a traditional finance broker, we work with a carefully selected network of private lenders, family offices and institutional investors to find funding solutions that suit each opportunity.
            </p>
            <p>
              Every finance enquiry goes through a structured review process. We assess each opportunity, prepare it for funding, and present it only to lenders who are a genuine fit. Your information remains confidential and is only shared with authorised counterparties who have agreed to protect your privacy.
            </p>
            <p>
              By approaching multiple funding partners in a controlled way, we create competition for your deal. This gives you access to a range of funding options while helping you seek suitable terms — without your proposal being circulated broadly across the market.
            </p>
          </div>
        </div>
        <div className="lg:col-span-2">
          <div
            className="gpc-card h-full"
            style={{ background: 'var(--gpc-paper-2)' }}
            data-testid="borrowers-coverage-card"
          >
            <div className="flex items-center gap-2 mb-5">
              <Briefcase className="w-4 h-4" style={{ color: 'var(--gpc-gold)' }} />
              <h3 className="gpc-h3" style={{ fontSize: '14px' }}>Capital we structure</h3>
            </div>
            <ul className="space-y-3">
              {CAPITAL_SOLUTIONS.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-3 text-[14px]"
                  style={{ color: 'var(--gpc-ink)', opacity: 0.86, fontFamily: 'Inter, system-ui, sans-serif' }}
                >
                  <span
                    className="w-1 h-1 rounded-full shrink-0"
                    style={{ background: 'var(--gpc-gold)' }}
                  />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Section>

    {/* Borrower pathway — labels match homepage Pathways exactly */}
    <Section testId="borrowers-pathway">
      <div className="max-w-3xl mb-10">
        <p className="gpc-eyebrow mb-4">Borrower pathway</p>
        <h2 className="gpc-h2 mb-4">
          How a transaction moves through the platform.
        </h2>
        <p className="gpc-body max-w-2xl">
          Every borrower mandate follows the same five-step workflow — designed to keep distribution selective, confidential and accountable.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {PATHWAY_STEPS.map((s, i) => <StepCard key={s.title} step={s} index={i} />)}
      </div>
    </Section>

    {/* Trust cues */}
    <Section alt testId="borrowers-trust">
      <p className="gpc-eyebrow mb-4">Controls in place</p>
      <h2 className="gpc-h2 mb-8">
        Confidentiality and control on every mandate.
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {TRUST_CUES.map((c) => (
          <div key={c.label} className="gpc-card flex items-start gap-3" data-testid={`borrowers-trust-${c.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
              style={{ background: 'rgba(201,168,106,0.10)' }}
            >
              <c.icon className="w-4 h-4" style={{ color: 'var(--gpc-gold)' }} />
            </div>
            <p
              className="text-[13px] mt-1.5"
              style={{ color: 'var(--gpc-ink)', fontFamily: 'Inter, system-ui, sans-serif' }}
            >
              {c.label}
            </p>
          </div>
        ))}
      </div>
    </Section>

    {/* Phase 105B — "What to include in your initial enquiry" checklist */}
    <Section testId="borrowers-enquiry-checklist">
      <div className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <p className="gpc-eyebrow mb-4">Before you send</p>
          <h2 className="gpc-h2 mb-4">
            What to include in your initial <span style={{ color: 'var(--gpc-gold)', fontStyle: 'italic' }}>enquiry.</span>
          </h2>
          <p className="gpc-body mb-4">
            To help us review your opportunity quickly, please include as much of the following as possible.
          </p>
          <p
            className="text-[13px]"
            style={{ color: 'var(--gpc-ink-soft)', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.6 }}
          >
            If you are unsure what to provide, send a short summary first. We will let you know what is required before any detailed review.
          </p>
        </div>
        <div className="lg:col-span-3">
          <div
            className="gpc-card"
            style={{ background: 'var(--gpc-paper-2)' }}
            data-testid="borrowers-enquiry-checklist-card"
          >
            <div className="flex items-center gap-2 mb-5">
              <ClipboardCheck className="w-4 h-4" style={{ color: 'var(--gpc-gold)' }} />
              <h3 className="gpc-h3" style={{ fontSize: '14px' }}>Include with your enquiry</h3>
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {ENQUIRY_CHECKLIST.map((item, i) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-[14px]"
                  style={{ color: 'var(--gpc-ink)', opacity: 0.88, fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.5 }}
                  data-testid={`borrowers-enquiry-item-${i + 1}`}
                >
                  <span
                    className="w-1 h-1 rounded-full shrink-0 mt-2"
                    style={{ background: 'var(--gpc-gold)' }}
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Section>

    {/* Phase 105B — Broker referral clarity */}
    <Section alt testId="borrowers-broker-referral">
      <div className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <p className="gpc-eyebrow mb-4">Referral partners</p>
          <h2 className="gpc-h2 mb-5">
            For brokers and <span style={{ color: 'var(--gpc-gold)', fontStyle: 'italic' }}>referral partners.</span>
          </h2>
          <div className="gpc-body space-y-4">
            <p>
              GP Capital works with brokers and referral partners on a clear, deal-by-deal basis.
            </p>
            <p>
              Where a broker introduces a transaction, the referral scope and any commercial arrangement should be agreed upfront in writing before the opportunity is progressed. The intention is to support the specific mandate being referred, not to take over the broker&apos;s broader client relationship.
            </p>
            <p>
              Commission splits, referral scope and communication protocols should be confirmed before engagement so all parties understand how the transaction will be handled.
            </p>
          </div>
        </div>
        <div className="lg:col-span-2">
          <div
            className="gpc-card h-full"
            data-testid="borrowers-broker-referral-card"
          >
            <div className="flex items-center gap-2 mb-5">
              <Users2 className="w-4 h-4" style={{ color: 'var(--gpc-gold)' }} />
              <h3 className="gpc-h3" style={{ fontSize: '14px' }}>Deal-by-deal, in writing</h3>
            </div>
            <ul className="space-y-3">
              {[
                'Referral scope agreed upfront',
                'Commercial terms confirmed in writing',
                'Communication protocols set',
                'Broker retains the broader client relationship',
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-[13.5px]"
                  style={{ color: 'var(--gpc-ink)', opacity: 0.86, fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.5 }}
                >
                  <span
                    className="w-1 h-1 rounded-full shrink-0 mt-2"
                    style={{ background: 'var(--gpc-gold)' }}
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p
              className="mt-5 text-[11px]"
              style={{
                color: 'var(--gpc-muted)',
                fontFamily: 'Space Grotesk, system-ui, sans-serif',
                letterSpacing: '0.04em',
                lineHeight: 1.5,
              }}
            >
              General website information only. Not a binding referral agreement. Individual terms are confirmed in writing on a deal-by-deal basis.
            </p>
          </div>
        </div>
      </div>
    </Section>

    {/* Phase 105B — Testimonial (gated).
        Renders only when TESTIMONIAL_APPROVED is flipped to true after
        Bren Watts confirms in writing. Uses the softened, outcome-neutral
        version only. The line that could imply outcome certainty is
        intentionally excluded. */}
    {TESTIMONIAL_APPROVED && (
      <Section testId="borrowers-testimonial">
        <div className="max-w-3xl">
          <p className="gpc-eyebrow mb-6">What a broker says</p>
          <Quote
            className="w-8 h-8 mb-6"
            style={{ color: 'var(--gpc-gold)' }}
            aria-hidden="true"
          />
          <blockquote
            className="gpc-body space-y-4 mb-8"
            style={{
              fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
              fontSize: 'clamp(1.05rem, 1.8vw, 1.25rem)',
              lineHeight: 1.55,
              color: 'var(--gpc-ink)',
              fontWeight: 400,
              letterSpacing: '-0.005em',
            }}
            data-testid="borrowers-testimonial-quote"
          >
            <p>
              &ldquo;I&apos;ve worked with a number of capital providers over the years, and GP Capital has been one of the most professional and efficient groups I&apos;ve dealt with.
            </p>
            <p>
              What really sets them apart is their platform. The process is streamlined, secure and easy to navigate. Being able to submit transactions, manage documentation and receive updates through one central platform removes a lot of the back-and-forth that can slow deals down.
            </p>
            <p>
              Giorgio is also excellent to work with. He is responsive, knowledgeable and understands both the lending and broker perspectives. Communication is clear, expectations are managed well, and the process feels structured and professional throughout.
            </p>
            <p>
              For brokers looking for a reliable capital partner with institutional-quality processes and strong service, I would be happy to recommend GP Capital.&rdquo;
            </p>
          </blockquote>
          <footer
            className="text-[13px]"
            style={{
              fontFamily: 'Space Grotesk, system-ui, sans-serif',
              color: 'var(--gpc-ink-soft)',
              letterSpacing: '0.04em',
            }}
            data-testid="borrowers-testimonial-attribution"
          >
            <span style={{ fontWeight: 600, color: 'var(--gpc-ink)' }}>Bren Watts</span>
            <span aria-hidden="true"> · </span>
            <span>FinanceX</span>
          </footer>
        </div>
      </Section>
    )}

    {/* Closing CTA */}
    <Section testId="borrowers-cta" className="relative overflow-hidden">
      <div className="max-w-2xl">
        <p className="gpc-eyebrow mb-4">Submit an enquiry</p>
        <h2 className="gpc-h2 mb-4">
          Discuss a funding requirement.
        </h2>
        <p className="gpc-body mb-3">
          Contact GP Capital to discuss your transaction and assess fit with the platform&apos;s capital network.
        </p>
        <p
          className="mb-8 text-[12px]"
          style={{
            color: 'var(--gpc-muted)',
            fontFamily: 'Space Grotesk, system-ui, sans-serif',
            letterSpacing: '0.04em',
          }}
        >
          All opportunities are indicative, subject to underwriting, diligence and definitive documentation.
        </p>
        <div className="flex flex-wrap gap-3" id="submit-deal-cta">
          <Link to="/contact" className="gpc-cta-primary" data-testid="for-borrowers-submit-cta">
            Submit an enquiry
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/portal/login" className="gpc-cta-secondary" data-testid="borrowers-cta-platform-bottom">
            Enter platform
          </Link>
        </div>
      </div>
    </Section>

    {/* Sticky mobile CTA — scrolls to the submit anchor */}
    <StickyMobileCTA
      label="Submit Enquiry"
      testid="sticky-submit-deal"
      onClick={() => {
        const el = document.getElementById('submit-deal-cta');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }}
    />
  </div>
);

export default ForBorrowersPage;
